// 检查点审核：左整页截图（可放大 / 看实时画面），右字段比对表（红黄在前）+ 费用；确认提交 / 让 AI 修改 / 手动接管 / 取消
import { useState } from "react";
import type { ReviewDTO, TaskDTO, UserDTO } from "@csp/shared";
import { api } from "../lib/api";
import { useMediaToken } from "../lib/media";
import { fieldLabel, REVIEWER_TEXT } from "../lib/format";
import { useAction } from "../lib/hooks";
import { mediaUrl } from "../lib/net";
import { approvalsOf, canReview, diffCounts, sortDiff } from "../lib/review";
import { Badge } from "./Badges";
import { useDialog } from "./Dialogs";
import { useLightbox } from "./Lightbox";
import { VncViewer } from "./VncViewer";

const FLAG = { match: "✓ 一致", mismatch: "✗ 不一致", uncertain: "? 不确定" } as const;

export function ReviewPanel({
  task,
  review,
  user,
  labels,
  takeover,
  onChanged,
  externalScreen = false,
}: {
  task: TaskDTO;
  review: ReviewDTO;
  user: UserDTO;
  labels: Record<string, string>;
  /** 运行处于接管状态（可操作实时画面） */
  takeover: boolean;
  onChanged: () => void;
  externalScreen?: boolean;
}) {
  const dialog = useDialog();
  const lb = useLightbox();
  const act = useAction();
  const [live, setLive] = useState(false);
  const [openGreen, setOpenGreen] = useState<Record<string, boolean>>({});
  const token = useMediaToken();
  const allowed = canReview(review.reviewer, user.role);
  const { whos: approvedBy, roles: approvedRoles } = approvalsOf(review);
  const iApproved = approvedRoles.has(user.role);
  const inTakeover = review.status === "takeover" || takeover;
  const counts = diffCounts(review.diff);
  const rows = sortDiff(review.diff);
  const shot = mediaUrl(review.screenshotUrl, token);
  const company = task.clientEntity.name || task.clientEntity.uen;

  const decide = async (action: Parameters<typeof api.decide>[1], note?: string) => {
    const r = await act.run(action, () => api.decide(review.id, action, note));
    if (r) onChanged();
  };

  const approve = async () => {
    const ok = await dialog.confirm({
      title: "确认提交？",
      okText: "确认提交",
      body: (
        <>
          <p>
            将为 <b>{company}</b>
            {task.clientEntity.uen && <>（UEN {task.clientEntity.uen}）</>} 正式提交「{task.typeName}」。
          </p>
          <p className="fee">
            <small>费用</small>
            {review.fee ?? "页面未显示"}
          </p>
          {counts.red > 0 && <p className="err">注意：还有 {counts.red} 项不一致！</p>}
          <p className="muted small">提交后不能撤回。</p>
        </>
      ),
    });
    if (ok) await decide("approve");
  };

  const revise = async () => {
    const note = await dialog.prompt({
      title: "让 AI 修改",
      label: "一句话说明要改什么",
      placeholder: "例如：单元号应为 #20-01",
      required: true,
      okText: "发给 AI",
    });
    if (note) await decide("revise", note);
  };

  const takeoverStart = async () => {
    const ok = await dialog.confirm({
      title: "手动接管修改",
      body: <p>打开可操作的实时画面，你直接在执行机浏览器里改。改完后回到这里点「接管完成，确认提交」。</p>,
      okText: "开始接管",
    });
    if (ok) await decide("takeover");
  };

  const takeoverDone = async () => {
    const ok = await dialog.confirm({
      title: "接管完成，确认提交？",
      okText: "确认提交",
      body: (
        <>
          <p>
            以当前页面为准，为 <b>{company}</b> 提交。
          </p>
          <p className="fee">
            <small>费用</small>
            {review.fee ?? "页面未显示"}
          </p>
          <p className="muted small">提交后不能撤回。</p>
        </>
      ),
    });
    if (ok) await decide("takeover_done");
  };

  const cancel = async () => {
    const note = await dialog.prompt({ title: "取消任务？", label: "原因（可不填）", okText: "取消任务", danger: true, cancelText: "不取消" });
    if (note !== null) await decide("cancel", note || undefined);
  };

  const disabled = !allowed || !!act.busy;

  return (
    <div data-testid="review-panel">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>检查点审核：{review.checkpointId}</h2>
        <Badge tone="gray">第 {review.round} 轮</Badge>
        <span className="muted small">审核人：{REVIEWER_TEXT[review.reviewer] ?? review.reviewer}</span>
        <span className="spacer" />
        {counts.red > 0 ? (
          <b className="err">{counts.red} 项不一致</b>
        ) : counts.yellow > 0 ? (
          <b className="warn-text">{counts.yellow} 项不确定</b>
        ) : (
          <b className="ok-text">字段全部一致</b>
        )}
      </div>
      {!allowed && review.reviewer === "boss" && <div className="notice warn">等待老板审核（在小程序里批）。</div>}
      {!allowed && review.reviewer !== "boss" && <div className="notice warn">这个检查点不归你审核。</div>}
      {review.reviewer === "both" && (
        <div className="notice info">
          需要文员和老板都批准。{approvedBy.length ? `已批准：${approvedBy.join("、")}` : "还没有人批准。"}
        </div>
      )}
      <div className="review-layout">
        <div>
          <div className="row" style={{ marginBottom: 8 }}>
            <b>整页截图</b>
            <span className="small muted">点图放大</span>
            <span className="spacer" />
            {!inTakeover && !externalScreen && (
              <button className="btn btn-small" onClick={() => setLive((v) => !v)}>
                {live ? "看截图" : "看实时画面"}
              </button>
            )}
          </div>
          {inTakeover && externalScreen ? <p className="notice">在左上方画面操作，完成后返回此处继续。</p> : inTakeover ? (
            <VncViewer taskId={task.id} mode="control" />
          ) : live ? (
            <VncViewer taskId={task.id} mode="view" />
          ) : shot ? (
            <div className="shot-box">
              <img className="shot" src={shot} alt="检查点整页截图" onClick={() => lb.open(shot)} data-testid="review-shot" />
            </div>
          ) : (
            <div className="empty">没有截图</div>
          )}
        </div>
        <div>
          {review.fee !== null && (
            <div className="fee" data-testid="fee">
              <small>费用</small>
              {review.fee}
            </div>
          )}
          <table className="tbl" data-testid="diff-table">
            <thead>
              <tr>
                <th>字段</th>
                <th>结果</th>
                <th>内容</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const expanded = d.result !== "match" || openGreen[d.field];
                return (
                  <tr key={d.field} className={`diff-row ${d.result}`} data-field={d.field} data-result={d.result}>
                    <td>{fieldLabel(d.field, labels)}</td>
                    <td className="diff-flag">{FLAG[d.result]}</td>
                    <td>
                      {expanded ? (
                        <div className="small">
                          <div>
                            <span className="muted">应为：</span>
                            <span translate={d.expected ? "no" : undefined}>{d.expected ?? "（空）"}</span>
                          </div>
                          <div>
                            <span className="muted">页面：</span>
                            <b translate={d.actual ? "no" : undefined}>{d.actual ?? "（没读到）"}</b>
                          </div>
                        </div>
                      ) : (
                        <button className="btn-link btn small" translate="no" onClick={() => setOpenGreen((s) => ({ ...s, [d.field]: true }))}>
                          {d.actual ?? d.expected}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted">此检查点没有配置比对字段</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {act.error && <div className="notice err" style={{ marginTop: 12 }}>{act.error}</div>}
      {review.hold ? <div className="btn-grid" style={{ marginTop: 16 }}><HoldButtons task={task} review={review} takeover={inTakeover} onChanged={onChanged} /></div> : <div className="btn-grid" style={{ marginTop: 16 }}>
        {inTakeover ? (
          <button className="btn btn-ok btn-big" disabled={disabled || user.role === "boss"} onClick={() => void takeoverDone()}>
            ✅ 接管完成，确认提交
          </button>
        ) : (
          <>
            <button className="btn btn-ok btn-big" disabled={disabled || iApproved} onClick={() => void approve()}>
              ✅ 确认提交
              {iApproved && <small>你已批准，等另一方</small>}
            </button>
            <button className="btn btn-review btn-big" disabled={disabled} onClick={() => void revise()}>
              ✏️ 让 AI 修改
            </button>
            {user.role !== "boss" && (
              <button className="btn btn-big" disabled={disabled} onClick={() => void takeoverStart()}>
                🖐 手动接管改
              </button>
            )}
          </>
        )}
        <button className="btn btn-danger btn-big" disabled={disabled} onClick={() => void cancel()}>
          ✖ 取消任务
        </button>
      </div>}
      {lb.node}
    </div>
  );
}

/** 探索 dry run 停在最终提交前：我来操作（实时画面变可操作，亲手检查、提交）/ 操作完了 / 结束（不提交） */
export function HoldButtons({ review, takeover, onChanged, small }: { task?: TaskDTO; review: ReviewDTO; takeover: boolean; onChanged: () => void; small?: boolean }) {
  const big = small ? "btn-small" : "btn-big";
  const dialog = useDialog();
  const act = useAction();
  const go = async (action: "takeover" | "takeover_done" | "end", note?: string) => {
    const r = await act.run(action, () => api.decide(review.id, action, note));
    if (r) onChanged();
  };
  return (
    <>
      {takeover ? (
        <button className={"btn btn-ok " + big} disabled={!!act.busy} onClick={() => void (async () => {
          const ok = await dialog.confirm({ title: "操作完了？", body: <p>系统会截一张当前画面存证，然后结束这次运行、关闭浏览器标签页。提交成功的话，请确认画面上已经出现参考号 / Appeal submitted。</p>, okText: "操作完了" });
          if (ok) await go("takeover_done");
        })()}>✅ 操作完了</button>
      ) : (
        <button className={"btn btn-primary " + big} disabled={!!act.busy} onClick={() => void go("takeover")}>🖐 我来操作</button>
      )}
      <button className={"btn " + big} disabled={!!act.busy} onClick={() => void (async () => {
        const ok = await dialog.confirm({ title: "结束，不提交？", body: <p>结束这次运行并关闭浏览器标签页。政府网站上的草稿会保留到截止时间，之后还可以从头重启接着做，或自己登录网站提交。</p>, okText: "结束（不提交）" });
        if (ok) await go("end");
      })()}>结束（不提交）</button>
      {act.error && <p className="notice err">{act.error}</p>}
    </>
  );
}
